import { isInvalidDate, toStringYYYYMMdd } from "@/utils/date";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import {
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Paper,
  Popper,
  SxProps,
  TextField,
  TextFieldProps,
  Theme,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { parse } from "date-fns";
import React, { ChangeEvent, useState } from "react";

type SCDesktopDatePickerProps = {
  sx?: SxProps<Theme>;
  variant: TextFieldProps["variant"];
  label: string;
  value: string | null;
  error?: TextFieldProps["error"];
  required?: TextFieldProps["required"];
  disabled?: TextFieldProps["disabled"];
  helperText?: TextFieldProps["helperText"];
  disableOpenPicker?: boolean;
  onChange: (value: string | null) => void;
  id?: TextFieldProps["id"];
};
export function SCDesktopDatePicker({
  sx,
  variant,
  label,
  value,
  error,
  required,
  disabled,
  helperText,
  disableOpenPicker,
  onChange,
  id,
}: SCDesktopDatePickerProps) {
  /**
   * 文字列の日付を {@link Date} に変換します。
   * 指定した文字列が null または空文字列の場合、null を返却します。
   * 日付に変換できなかった場合は返却値の Date は Invalid Date になります。
   * @returns Date | null
   */
  function parseStringDate(value: string | null): Date | null {
    if (value == null) return null;
    if (value.length === 8) {
      return parse(value, "yyyyMMdd", new Date());
    } else if (value.length === 10) {
      return parse(value, "yyyy-MM-dd", new Date());
    } else {
      return null;
    }
  }

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);

  // カレンダーの出し入れ
  const handleIconClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(isOpen ? null : event.currentTarget);
  };
  const handleClickAway = () => {
    setAnchorEl(null);
  };

  // 入力対応
  const valueChange = (keyboardInputValue: string) => {
    const dateValue = parseStringDate(keyboardInputValue);
    if (dateValue != null && !isInvalidDate(dateValue)) {
      onChange(toStringYYYYMMdd(dateValue, "-"));
    } else {
      onChange(keyboardInputValue ?? null);
    }
  };
  // キーボード入力
  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    valueChange(e.target.value);
  };
  // カレンダーによる入力
  const handleCalendarChange = (newValue: Date | null) => {
    if (newValue) {
      valueChange(toStringYYYYMMdd(newValue, ""));
      setAnchorEl(null);
    }
  };

  return (
    <>
      <TextField
        label={label}
        // value={value}
        value={value ?? ""}
        variant={variant}
        sx={sx}
        error={error}
        required={required}
        disabled={disabled}
        helperText={helperText}
        id={id}
        onChange={handleTextChange}
        placeholder="yyyy-MM-dd"
        InputProps={
          disableOpenPicker
            ? {}
            : {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleIconClick} edge="end">
                      <CalendarMonthOutlinedIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }
        }
      />

      <Popper
        open={isOpen}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{
          // Dialog の z-index (theme.zIndex.modal) よりも 1 大きい値を設定
          zIndex: (theme) => theme.zIndex.modal + 1,
        }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper>
            <DateCalendar
              value={parseStringDate(value)}
              onChange={handleCalendarChange}
            />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
